import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invitesApi } from '@/lib/api';
import type { Invite, InviteListItem, ValidateInviteResponse, GlobalRole } from '@/types';

export function useInvites() {
  return useQuery({
    queryKey: ['invites'],
    queryFn: async () => {
      const response = await invitesApi.getAll();
      return response.data as InviteListItem[];
    },
  });
}

export function useCreateInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { email: string; role?: GlobalRole }) => {
      const response = await invitesApi.create(data);
      return response.data as Invite;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
    },
  });
}

export function useValidateInvite(token: string | undefined) {
  return useQuery({
    queryKey: ['invite', 'validate', token],
    queryFn: async () => {
      if (!token) throw new Error('Token required');
      const response = await invitesApi.validate(token);
      return response.data as ValidateInviteResponse;
    },
    enabled: !!token,
    retry: false,
  });
}

export function useAcceptInvite() {
  return useMutation({
    mutationFn: async (data: { token: string; displayName: string; password: string }) => {
      const response = await invitesApi.accept(data);
      return response.data as { message: string; email: string };
    },
  });
}

export function useRegenerateInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await invitesApi.regenerate(id);
      return response.data as Invite;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
    },
  });
}

export function useDeleteInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await invitesApi.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
    },
  });
}
